/**
 * AssistantMessage Component
 *
 * Renders AI assistant messages in the conversation view matching the Figma design.
 * Features left-aligned layout, white background with border, agent tags, and citations.
 *
 * Features:
 * - Left-aligned layout as per Figma design
 * - White background with border (#CCD2DB)
 * - Agent tag display (e.g., "Knowledge Agent")
 * - Citation references with numbered badges
 * - Message actions (copy, bookmark, regenerate, etc.)
 * - Streaming text support with loading indicators
 * - Markdown rendering for rich content
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Text,
  Group,
  Loader,
  Button,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import { MessageAttachment } from '../MessageAttachment';
import { MessageMarkdown, CitationList } from '../MessageMarkdown';
import type { Citation } from '../MessageMarkdown';
import { safelyOpenUrl, isWebLink, trackCitationClick } from '../MessageMarkdown/utils/urlValidation';
import {
  MessageActions,
  createCopyAction,
  createLikeAction,
  createDislikeAction,
  createShowThoughtProcessAction,
  createBranchInNewChatAction,
} from '../MessageActions';
import { useConversationDetail } from '../../../../hooks/useConversationDetail';
import { useThoughtProcessQuery } from '../../../../hooks/useThoughtProcessQuery';
import type { MessageForFeedback } from '../../../../utils/feedbackTransform';
import { branchConversation, conversationQueryKeys } from '../../../../api/conversations';
import classes from './AssistantMessage.module.css';
import type {
  MessageComponentProps,
  MessageActionType,
  Reference,
} from '../../types/conversation';
import { useStreamingStore, type ToolCallRecord } from '../../../../stores/streamingStore';
import { trackComponentInteraction } from '../../../../utils/analytics';
import { AgentChunkAccordion } from './AgentChunkAccordion';
import { ImageGenerationLoadingCard } from './ImageGenerationLoadingCard';
import { generateConversationId } from '../../../../utils/uuid';
import { useConversationStore } from '../../../../stores/conversationStore';
import { useChatStore } from '../../../../stores/chatStore';
import { showNotification } from '../../../../components/Notification';

// =============================================================================
// COMPONENT INTERFACES
// =============================================================================

interface AssistantMessageState {
  showMenu: boolean;
  extractedCitations: Citation[];
}

// =============================================================================
// STYLES
// =============================================================================

// Using CSS modules for consistent styling approach

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AssistantMessage: React.FC<MessageComponentProps> = ({
  message,
  showActions = true,
  onAction,
  conversationId,
  className,
  style,
  showCheckResult = false,
  onCheckResult,
  isCheckingResult = false,
}) => {
  // =============================================================================
  // STATE AND REFS
  // =============================================================================

  // Using CSS modules instead of CSS-in-JS
  const [state, setState] = useState<AssistantMessageState>({
    showMenu: false,
    extractedCitations: [],
  });

  const [messageDelivered, setMessageDelivered] = useState<boolean>(() => {
    return Boolean(message.content && message.content.trim());
  })
  const [isBranching, setIsBranching] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isBranching) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isBranching]);

  const currentConversation = useConversationStore((state) =>
    conversationId
      ? state.conversations.find((conversation) => conversation.conversationID === conversationId)
      : undefined
  );
  const hasLocalUploadedFiles = useChatStore((chatState) => {
    const fileState = conversationId ? chatState.conversationFiles[conversationId] : undefined;
    return Boolean(
      fileState?.uploadedFiles.length ||
      fileState?.pendingForMessage.length ||
      fileState?.pendingFiles.length ||
      fileState?.fileEntries?.some((entry) => entry.status !== 'deleting' && entry.status !== 'error')
    );
  });

  const { isStreaming: storeIsStreaming, currentStreamRecord, lastDoneResponse } = useStreamingStore();
  const baseMessageId = useMemo(
    () => message.id.replace(/-assistant$/, ''),
    [message.id],
  );
  const hasNoContent = !(message.content && message.content.trim());
  const lastDoneMessageId = typeof lastDoneResponse?.message_id === 'string' ? lastDoneResponse.message_id : '';
  const lastDoneStreamId = typeof lastDoneResponse?.stream_id === 'string' ? lastDoneResponse.stream_id : '';
  const isCurrentStreamMessage = currentStreamRecord?.messageId === baseMessageId;
  const currentRecordHasAnswer = Boolean(currentStreamRecord?.answer?.trim());
  const isLastDoneStreamMessage = lastDoneStreamId === baseMessageId || lastDoneMessageId === baseMessageId;
  const isLikelyHomeStreamMessage = hasNoContent && !messageDelivered && Boolean(currentStreamRecord) && (storeIsStreaming || currentRecordHasAnswer);
  const isCompletedStreamMessage = Boolean(
    currentStreamRecord?.complete &&
    !storeIsStreaming &&
    (isCurrentStreamMessage || isLastDoneStreamMessage || isLikelyHomeStreamMessage)
  );
  // Fallback: if no ID match but this message has no content and the store is
  // actively streaming (or has just received an answer before done), assume
  // this is the target assistant message. This handles the home-screen path
  // where the optimistic assistant message ID differs from the stream ID.
  const fallbackStreamMatch = !isCurrentStreamMessage && hasNoContent && currentStreamRecord && !currentStreamRecord.complete && (storeIsStreaming || currentRecordHasAnswer);
  const scopedStreamRecord = isCurrentStreamMessage || fallbackStreamMatch || isCompletedStreamMessage ? currentStreamRecord : null;

  const liveToolCalls = useMemo<ToolCallRecord[]>(() => {
    return (scopedStreamRecord?.toolCalls || []).filter((tc) => tc.agent && !/^orchestrator\s*agent$/i.test(tc.agent));
  }, [scopedStreamRecord]);

  // Build tool call chunks for legacy/image checks; duplicate agent calls are displayed via liveToolCalls.
  const liveToolChunks = useMemo(() => {
    const chunks: Record<string, string> = {};
    for (const tc of liveToolCalls) {
      chunks[tc.agent] = `${chunks[tc.agent] || ''}${tc.content}`;
    }
    return chunks;
  }, [liveToolCalls]);

  // Historical tool calls from the cached conversation message
  const cachedToolCalls = useMemo<ToolCallRecord[] | null>(() => {
    const toolCalls = (message as any).toolCalls;
    if (!toolCalls || !Array.isArray(toolCalls) || toolCalls.length === 0) return null;
    const calls = toolCalls
      .filter((tc: Partial<ToolCallRecord>) => tc.agent && !/^orchestrator\s*agent$/i.test(tc.agent))
      .map((tc: Partial<ToolCallRecord>, idx: number) => ({
        id: tc.id || `cached_${tc.agent}_${idx}`,
        agent: tc.agent || '',
        tool: tc.tool || '',
        args: tc.args || {},
        content: tc.content || '',
        status: tc.status || 'done',
        startedAt: tc.startedAt || 0,
        completedAt: tc.completedAt,
      }));
    return calls.length > 0 ? calls : null;
  }, [(message as any).toolCalls]);

  const doneToolCalls = useMemo<ToolCallRecord[]>(() => {
    const toolCalls = isLastDoneStreamMessage && Array.isArray(lastDoneResponse?.toolCalls) ? lastDoneResponse.toolCalls : [];
    return toolCalls
      .filter((tc: Partial<ToolCallRecord>) => tc.agent && !/^orchestrator\s*agent$/i.test(tc.agent))
      .map((tc: Partial<ToolCallRecord>, idx: number) => ({
        id: tc.id || `done_${tc.agent}_${idx}`,
        agent: tc.agent || '',
        tool: tc.tool || '',
        args: tc.args || {},
        content: tc.content || '',
        status: tc.status || 'done',
        startedAt: tc.startedAt || 0,
        completedAt: tc.completedAt,
      }));
  }, [isLastDoneStreamMessage, lastDoneResponse]);

  const normalizeToolContent = (value: string | undefined | null) => (value || '').replace(/\s+/g, ' ').trim();

  const filterDuplicateToolCalls = (toolCalls: ToolCallRecord[] | null, finalContent: string) => {
    if (!toolCalls) return null;
    // For single-tool calls we always keep the entry so the Show-Thinking
    // accordion has content, even when the tool output equals the final
    // answer verbatim (e.g. @difot Genie, @file SafeCoopFile). Dedup only
    // kicks in when there are 2+ tool calls.
    if (toolCalls.length <= 1) {
      return toolCalls.filter((tc) => normalizeToolContent(tc.content)).length > 0
        ? toolCalls
        : null;
    }
    const normalizedFinal = normalizeToolContent(finalContent);
    const filtered = toolCalls.filter((tc) => {
      const normalizedToolContent = normalizeToolContent(tc.content);
      return normalizedToolContent && (!normalizedFinal || normalizedToolContent !== normalizedFinal);
    });
    return filtered.length > 0 ? filtered : null;
  };

  // Auto-fetch thought process to populate tool call accordion when no cached/live data.
  // Only fetch for the LATEST message to avoid firing N API calls for old conversations.
  const hasAnyCachedTools = cachedToolCalls !== null;
  const hasAnyLiveTools = liveToolCalls.length > 0 || doneToolCalls.length > 0;
  // Read-only lookup of already-fetched data; don't let this secondary subscription
  // trigger its own background refetch that could race a newer streamed answer.
  const { data: conversationDetail } = useConversationDetail(conversationId || '', {
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  const hasUploadedFiles = useMemo(() => {
    const history = conversationDetail?.conversationHistory;
    return hasLocalUploadedFiles || (
      Array.isArray(history) && history.some((historyMessage) => (
        Array.isArray(historyMessage.uploadedFiles) && historyMessage.uploadedFiles.length > 0
      ))
    );
  }, [conversationDetail, hasLocalUploadedFiles]);
  const isLatestMessage = useMemo(() => {
    const history = conversationDetail?.conversationHistory;
    if (!history || !Array.isArray(history) || history.length === 0) return false;
    const lastMsg = history[history.length - 1];
    return lastMsg?.message_id === baseMessageId;
  }, [conversationDetail, baseMessageId]);
  const shouldFetchThoughtProcess = Boolean(
    conversationId &&
    baseMessageId &&
    !hasAnyCachedTools &&
    !hasAnyLiveTools &&
    messageDelivered &&
    isLatestMessage &&
    !isCompletedStreamMessage
  );
  const { data: thoughtProcessData } = useThoughtProcessQuery(
    { conversationID: conversationId || '', messageID: baseMessageId },
    { enabled: shouldFetchThoughtProcess },
  );

  const thoughtProcessChunks = useMemo(() => {
    if (!thoughtProcessData?.thoughtprocess) return null;
    const content = thoughtProcessData.thoughtprocess;
    const regex = /###\s+Agent:\s*([^\n]+)\n([\s\S]*?)(?=(?:\n{0,2}---\n\n)?###\s+Agent:|\s*$)/gi;
    const chunks: Record<string, string> = {};
    let match;
    while ((match = regex.exec(content)) !== null) {
      const agentName = match[1].trim();
      const agentContent = match[2].trim();
      if (agentName && agentContent && !/^orchestrator\s*agent$/i.test(agentName)) {
        chunks[agentName] = agentContent;
      }
    }
    return Object.keys(chunks).length > 0 ? chunks : null;
  }, [thoughtProcessData]);

  useEffect(() => {
    setMessageDelivered(Boolean(message.content && message.content.trim()));
  }, [message.id]);

  // Find the parent user message from the conversation context
  const parentMessage = useMemo(() => {
    if (!conversationDetail?.conversationHistory) {
      return undefined;
    }

    // Extract the base message_id from the assistant message ID
    // Assistant message IDs are in format: `${message_id}-assistant`
    const baseMessageId = message.id.replace('-assistant', '');

    // Handle the case where conversationHistory might be a single object or an array
    const conversationHistory = Array.isArray(conversationDetail.conversationHistory)
      ? conversationDetail.conversationHistory
      : [conversationDetail.conversationHistory];

    // Find the message in conversation history that matches the base message ID
    const conversationMessage = conversationHistory.find(
      (msg: any) => msg.message_id === baseMessageId
    );

    if (conversationMessage?.user) {
      return {
        content: conversationMessage.user,
        role: 'user' as const,
      };
    }

    return undefined;
  }, [conversationDetail, message.id, message.content, message.role, message.metadata]);

  useEffect(() => {
    if (!message.content || !message.content.trim()) {
      setState(prev => ({ ...prev, extractedCitations: [] }));
      setMessageDelivered(false);
      return;
    }
    setMessageDelivered(true)
  }, [message.content]);

  // =============================================================================
  // DERIVED STATE
  // =============================================================================


  // Fetch streaming state from the zustand store
  // Only consider this message as actively streaming if it has no delivered content yet
  const hasContent = (message.content || '').trim().length > 0;
  const isThisMessageStreaming = storeIsStreaming && !hasContent && !messageDelivered;
  const isStreaming = message.isStreaming || isThisMessageStreaming;
  const imageToolCall = liveToolCalls.find((tc) =>
    /image/i.test(tc.agent || '') || /image/i.test(tc.tool || '')
  );
  const imageAgentChunk = imageToolCall?.content || liveToolChunks['Image Agent'] || '';
  const statusMessages = scopedStreamRecord?.statusMessages || [];
  const latestStatusMessage = statusMessages.length > 0 ? statusMessages[statusMessages.length - 1] : '';
  const imageProgressText = `${imageAgentChunk}\n${latestStatusMessage}`;
  const hasImageStreamFeedback =
    Boolean(imageToolCall) ||
    /image/i.test(latestStatusMessage) ||
    /Generating image|Editing image|Uploading generated image/i.test(imageAgentChunk);
  const isImageLoading = isStreaming && !hasContent && hasImageStreamFeedback;
  const imageLoadingLabel = useMemo(() => {
    if (/Uploading generated image/i.test(imageProgressText)) {
      return 'Uploading generated image…';
    }
    if (/Editing image/i.test(imageProgressText)) {
      return 'Editing image…';
    }
    return 'Generating image…';
  }, [imageProgressText]);
  const hasGeneratedImage = (message.content || '').includes('![Generated image]');
  const isImageMessageLayout = isImageLoading || hasGeneratedImage;

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleAction = (actionType: MessageActionType) => {
    trackComponentInteraction(
          actionType,
          actionType,
          'button',
          'click',
          actionType
        );
    onAction?.(actionType, message.id);
  };

  // Transform message for feedback compatibility
  const messageForFeedback: MessageForFeedback = {
    id: message.id,
    content: message.content,
    role: message.role,
    metadata: {
      originalMessageId: message.id, // Use the message ID as the original ID
      ...message.metadata,
    },
    // Include parent message if found from conversation context
    ...(parentMessage && { parentMessage }),
    genie_space_names: message.genie_space_names,
    genie_conversation_id: message.genie_conversation_id,
    genie_message_id: message.genie_message_id,
  };

  const handleCopy = async () => {
    try {
      // Get the rendered HTML from the message container
      const messageElement = document.getElementById(`message-${message.id}`);
      let renderedHtml = '';

      if (messageElement) {
        // Find the markdown content container
        const markdownContainer = messageElement.querySelector(`.${classes.content}`);
        if (markdownContainer) {
          // Get the rendered HTML (clone to avoid modifying the DOM)
          renderedHtml = markdownContainer.innerHTML;
        }
      }

      // Fallback to simple div if we couldn't get the rendered HTML
      if (!renderedHtml) {
        renderedHtml = `<div>${message.content}</div>`;
      }

      // Create a properly formatted HTML document for email clients
      const formattedHtml = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #333;">
          ${renderedHtml}
        </div>
      `;

      const sanitizedHtmlString = DOMPurify.sanitize(formattedHtml);

      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([sanitizedHtmlString], { type: "text/html" }),
          "text/plain": new Blob([message.content], { type: "text/plain" })
        })
      ]);
      handleAction('copy');
    } catch (error) {
      // Fallback for older browsers
      try {
        await navigator.clipboard.writeText(message.content);
        handleAction('copy');
      } catch (fallbackError) {
        console.error('Failed to copy message:', fallbackError);
      }
    }
  };

  const handleLike = () => {
    handleAction('like');
  };

  const handleDislike = () => {
    handleAction('dislike');
  };

  const handleShowThoughtProcess = () => {
    handleAction('show_thought_process');
  };

  const handleBranchInNewChat = async () => {
    if (!conversationId || isBranching || hasUploadedFiles) {
      return;
    }

    const history = conversationDetail?.conversationHistory;
    if (!history || !Array.isArray(history) || history.length === 0) {
      showNotification('Unable to branch this message because conversation history is unavailable.');
      return;
    }

    const turnIndex = history.findIndex((turn) => turn.message_id === baseMessageId);
    if (turnIndex < 0) {
      showNotification('Unable to branch this message. Please try again.');
      return;
    }

    const newConversationId = generateConversationId();
    const branchedHistory = history.slice(0, turnIndex + 1);
    const baseTitle = (currentConversation?.conversationTitle || branchedHistory[0]?.user || 'New Chat').trim();
    const titleWithoutBranchSuffix = baseTitle.replace(/\s+Branch$/i, '').trim();
    const newConversationTitle = `${titleWithoutBranchSuffix || 'New Chat'} Branch`.slice(0, 200);

    setIsBranching(true);
    try {
      await branchConversation({
        newConversationId,
        newConversationTitle,
        conversationHistory: branchedHistory,
        profile: currentConversation?.profile,
        category: currentConversation?.category || {},
        genie_conversation_ids: conversationDetail?.genie_conversation_ids,
        genie_space_names: conversationDetail?.genie_space_names,
        genie_conversation_id: conversationDetail?.genie_conversation_id,
        foundry_rag_conversation_ids: conversationDetail?.foundry_rag_conversation_ids,
        websearch_conversation_id: conversationDetail?.websearch_conversation_id,
      });

      // Prime cache for fast first render in the branched tab.
      queryClient.setQueryData(conversationQueryKeys.detail(newConversationId), {
        conversationID: newConversationId,
        conversationHistory: branchedHistory,
        genie_conversation_ids: conversationDetail?.genie_conversation_ids,
        genie_space_names: conversationDetail?.genie_space_names,
        genie_conversation_id: conversationDetail?.genie_conversation_id,
        foundry_rag_conversation_ids: conversationDetail?.foundry_rag_conversation_ids,
        websearch_conversation_id: conversationDetail?.websearch_conversation_id,
      });
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.lists() });

      trackComponentInteraction(
        'Branch in new chat',
        'branch_in_new_chat',
        'menu_item',
        'click',
        'branch_conversation'
      );

      const branchUrl = `/chat/${newConversationId}`;
      window.location.href = branchUrl;
    } catch (error) {
      console.error('Failed to branch conversation:', error);
      showNotification('Could not create branch. Please try again.');
    } finally {
      setIsBranching(false);
    }
  };

  const effectiveDataPoints = useMemo(() => {
    const messageMetadata = message.metadata as any;
    const streamMetadata = scopedStreamRecord?.metadata as any;
    const candidates = [
      messageMetadata?.dataPoints,
      messageMetadata?.data_points,
      messageMetadata?.citations,
      streamMetadata?.dataPoints,
      streamMetadata?.data_points,
      isLastDoneStreamMessage ? lastDoneResponse?.dataPoints : null,
      isLastDoneStreamMessage ? lastDoneResponse?.data_points : null,
    ];
    const dataPoints = candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0);
    return Array.isArray(dataPoints) ? dataPoints : [];
  }, [message.metadata, scopedStreamRecord?.metadata, isLastDoneStreamMessage, lastDoneResponse]);

  const effectiveSourceReferences = useMemo<Reference[]>(() => {
    return effectiveDataPoints
      .map((dp: any) => ({
        title: dp?.title || dp?.text || dp?.source || '',
        content: dp?.content || dp?.snippet || dp?.preview || '',
        source: dp?.source || dp?.url || '',
      }))
      .filter((dp: Reference) => Boolean(dp.title && dp.content));
  }, [effectiveDataPoints]);

  // Build a normalised title→url lookup from backend dataPoints once
  const dataPointUrlMap = useMemo(() => {
    const norm = (s: string) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const map = new Map<string, string>();
    for (const dp of effectiveDataPoints) {
      const title = norm(dp?.title);
      const url = dp?.url || dp?.source;
      if (title && url) map.set(title, url);
    }
    return { map, norm };
  }, [effectiveDataPoints]);

  const dataPointCitations = useMemo<Citation[]>(() => {
    return effectiveDataPoints
      .map((dp: any, index: number) => {
        const text = dp?.title || dp?.text || dp?.source || `Source ${index + 1}`;
        const rawUrl = dp?.url || dp?.source;
        const url = typeof rawUrl === 'string' ? rawUrl : '';
        return {
          id: String(dp?.id || url || text || index),
          text,
          url: isWebLink(url) ? url : undefined,
          index: index + 1,
        };
      })
      .filter((citation: Citation) => citation.text);
  }, [effectiveDataPoints]);

  /**
   * Resolve a valid URL for a citation.  Tries, in order:
   *   1. The URL already on the citation object (if it's a valid web link)
   *   2. A match from the backend dataPoints by title
   * Returns the resolved URL or undefined.
   */
  const resolveUrl = (citation: Citation): string | undefined => {
    if (citation.url && isWebLink(citation.url)) return citation.url;
    const { map, norm } = dataPointUrlMap;
    const fallback = map.get(norm(citation.text));
    if (fallback && isWebLink(fallback)) return fallback;
    return undefined;
  };

  const handleCitationsExtracted = (citations: Citation[]) => {
      // Patch citations whose URL is missing or not a valid web link
      // by cross-referencing against backend dataPoints.
      const patched = citations.map(c => {
        const url = resolveUrl(c);
        return url && url !== c.url ? { ...c, url } : c;
      });
      setState(prev => ({ ...prev, extractedCitations: patched }));
  };

  const handleCitationClick = (citation: Citation) => {
    // Track the citation click for analytics
    trackCitationClick(citation);

    // Resolve URL — prefer the citation's own URL if valid, otherwise
    // fall back to the matching backend dataPoint.
    const url = resolveUrl(citation);
    if (url) {
      const opened = safelyOpenUrl(url);
      if (opened) {
        // Trigger action callback to notify parent components
        handleAction('citation_click');
      }
    } else {
      console.warn('Citation has no valid URL to open:', citation.text);
    }
  };

  // =============================================================================
// RENDER HELPERS
// =============================================================================

  const renderContent = () => {
    if (isImageLoading) {
      return <ImageGenerationLoadingCard label={imageLoadingLabel} />;
    }

    const doneAnswer = isLastDoneStreamMessage && typeof lastDoneResponse?.answer === 'string' ? lastDoneResponse.answer : '';
    const liveAnswer = scopedStreamRecord?.answer || doneAnswer || '';
    const effectiveLiveToolCalls = liveToolCalls.length > 0 ? liveToolCalls : doneToolCalls;
    const visibleLiveToolCalls = filterDuplicateToolCalls(effectiveLiveToolCalls, liveAnswer || message.content || '') || [];
    const showLiveToolCalls = visibleLiveToolCalls.length > 0;
    const isStreamingWithoutContent = isStreaming && (!message.content || !message.content.trim()) && !messageDelivered;

    // Actively streaming — show status, live accordion + answer text/cursor
    if (isStreamingWithoutContent) {
      const hasNoVisibleContent = !latestStatusMessage && !showLiveToolCalls && !liveAnswer;
      // Single tool call: skip accordion, show query as status + stream answer directly
      const isSingleToolCall = visibleLiveToolCalls.length === 1;
      const singleToolQuery = isSingleToolCall
        ? (visibleLiveToolCalls[0].args as Record<string, unknown>)?.query as string | undefined
        : undefined;

      return (
        <>
          {latestStatusMessage && (
            <Text size="sm" c="dimmed" mb="xs">{latestStatusMessage}</Text>
          )}
          {isSingleToolCall && singleToolQuery && (
            <Text size="sm" c="dimmed" mb="xs">Searching: {singleToolQuery}</Text>
          )}
          {showLiveToolCalls && (
            <AgentChunkAccordion
              toolCalls={visibleLiveToolCalls}
              dataPoints={effectiveSourceReferences}
              isStreaming={isStreaming}
              scopeId={`${message.id}:live-tool-calls`}
            />
          )}
          {hasNoVisibleContent ? (
            <Box className={`${classes.content} ${classes.streamingCursor}`} />
          ) : (
            <MessageMarkdown
              content={liveAnswer + '\u258C'}
              className={`${classes.content} ${classes.streamingContent}`}
              skipEnhancements
            />
          )}
        </>
      );
    }

    // Delivered — show cached tool calls (collapsible) above the final answer
    const enrichedContent = message.content || liveAnswer;
    const toolCallsToShow = cachedToolCalls || (liveToolCalls.length > 0 ? liveToolCalls : (doneToolCalls.length > 0 ? doneToolCalls : null));
    const visibleToolCalls = filterDuplicateToolCalls(toolCallsToShow, enrichedContent);
    const visibleThoughtProcessChunks =
      thoughtProcessChunks && Object.keys(thoughtProcessChunks).length > 0 ? thoughtProcessChunks : null;

    // Always show the Show-Thinking accordion when we have tool-call content,
    // even for single-agent calls. The accordion holds the raw tool output
    // (RAG chunks, Genie tables, etc.) which is distinct from the synthesised
    // outer answer. We still surface a subtle agent-name attribution above
    // the answer for single-agent cases.
    const isSingleDeliveredToolCall = visibleToolCalls && visibleToolCalls.length === 1;
    const isSingleThoughtProcessAgent = !visibleToolCalls && visibleThoughtProcessChunks && Object.keys(visibleThoughtProcessChunks).length === 1;
    const isSingleAgent = isSingleDeliveredToolCall || isSingleThoughtProcessAgent;
    const singleAgentName = isSingleDeliveredToolCall
      ? visibleToolCalls[0].agent
      : isSingleThoughtProcessAgent
        ? Object.keys(visibleThoughtProcessChunks!)[0]
        : null;

    return (
      <>
        {isSingleAgent && singleAgentName && (
          <Text size="xs" c="dimmed" mb={4}>{singleAgentName}</Text>
        )}
        {visibleToolCalls ? (
          <AgentChunkAccordion
            toolCalls={visibleToolCalls}
            dataPoints={effectiveSourceReferences}
            collapseByDefault
            scopeId={`${message.id}:tool-calls`}
          />
        ) : visibleThoughtProcessChunks && (
          <AgentChunkAccordion
            chunks={visibleThoughtProcessChunks}
            dataPoints={effectiveSourceReferences}
            collapseByDefault
            scopeId={`${message.id}:thought-process`}
          />
        )}
        <MessageMarkdown
          content={enrichedContent}
          enableImageDownload
          onCitationClick={handleCitationClick}
          onFollowUpClick={(question) => {
            console.log('Follow-up question clicked:', question);
          }}
          onCitationsExtracted={handleCitationsExtracted}
          className={classes.content}
        />
      </>
    );
  };

  const renderCitations = () => {
    const citations = state.extractedCitations.length > 0 ? state.extractedCitations : dataPointCitations;
    if (citations.length === 0) return null;

    return (
      <CitationList
        citations={citations}
        onCitationClick={handleCitationClick}
        className={classes.citationsContainer}
      />
    );
  };

  const renderActions = () => {
    if (!showActions || isStreaming) return null;

    // Create primary actions
    const primaryActions = [
      createCopyAction(handleCopy),
    ];

    // Create secondary actions
    const secondaryActions = [
      createLikeAction(handleLike, message.actions.isLiked),
      createDislikeAction(handleDislike, message.actions.isDisliked),
    ];

    // Create menu actions - always show thought process for assistant messages
    const menuActions = [
      ...(hasUploadedFiles ? [] : [createBranchInNewChatAction(handleBranchInNewChat, isBranching)]),
      createShowThoughtProcessAction(handleShowThoughtProcess),
    ];

    return (
      <Group gap="sm" align="center" wrap="nowrap">
        <MessageActions
          primaryActions={primaryActions}
          secondaryActions={secondaryActions}
          menuActions={menuActions}
          showSpacer={true}
          className={classes.actions}
          message={messageForFeedback}
          conversationId={conversationId}
          // TODO: Add userName from auth store when available
        />
      </Group>
    );
  };


  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Box
      className={`${classes.container} ${className || ''}`}
      style={style}
      aria-busy={isBranching}
    >
      {/* Attachments */}
      {message.metadata?.attachments?.map((attachment) => (
        <MessageAttachment
          key={attachment.id}
          attachment={attachment}
          // onDownload={onDownloadAttachment}
          // onPreview={onPreviewAttachment}
        />
      ))}

      {/* Message Box */}
      <Box
        className={`${classes.messageBox} ${isImageMessageLayout ? classes.imageMessage : ''}`}
        id={`message-${message.id}`}
      >
        {/* Message Content */}
        {renderContent()}
        {showCheckResult && onCheckResult && (
          <Button
            mt="md"
            variant="outline"
            leftSection={<IconRefresh size={16} aria-hidden />}
            onClick={() => {
              trackComponentInteraction('AssistantMessage', 'Check result', 'button', 'click', 'digital_maintenance_check_result');
              onCheckResult();
            }}
            disabled={isCheckingResult}
          >
            Check result
          </Button>
        )}

        {/* Citations */}
        {renderCitations()}

        {/* Actions */}
        <Box className={classes.actionsWarningRow}>
          {renderActions()}
        </Box>

      </Box>

      {isBranching && (
        <Box className={classes.branchingOverlay} role="dialog" aria-modal="true" aria-labelledby="branching-status-title">
          <Group className={classes.branchingStatus} gap="md" wrap="nowrap">
            <Loader size="md" />
            <Box>
              <Text id="branching-status-title" fw={600}>Opening new chat</Text>
              <Text size="sm" c="dimmed">Please wait while your conversation is prepared.</Text>
            </Box>
          </Group>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default AssistantMessage;
export type { AssistantMessageState };
